import React, { useContext, useMemo, useState } from 'react';

import { isGoGameEnv } from 'App/constants';
import { useCurrentUser } from 'Auth';

import {
  SidePage,
  Tabs,
  Tab,
  Box,
  Divider,
  LegacySwitchView,
} from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';

import ManageGroups from '../ManageGroups/ManageGroups';
import PresetList from '../PresetList';
import { PresetsContext } from '../PresetsContext';
import { ACTION_SHARE_GROUP } from '../components/GroupActions/constants';
import { useIsUpdating, useUpdateUsageDatePreset } from '../data';
import { useIsOwn } from '../data/useIsOwn';

const OWN = 'own';
export const SHARED = 'shared';

const PresetsSidePage = ({ setOrderBy, onClose, onExited, open }) => {
  const {
    orderBy,
    grouped,
    presetKey,
    activePreset,
    ownPresets,
    publicPresets,
    isLoading,
    isRefetching,
    isManageGroups,
    onApply,
  } = useContext(PresetsContext);
  const isOwn = useIsOwn();

  const [activeTabId, setActiveTabId] = useState(() => {
    if (!activePreset) {
      return OWN;
    }
    if (isOwn({ id: activePreset?.user?.id })) {
      return OWN;
    } else {
      return SHARED;
    }
  });

  const [childComponent, showChildComponent] = useChildComponent();

  const [updateUsageDatePreset] = useUpdateUsageDatePreset({
    presetKey,
    orderBy,
  });

  const isUpdating = useIsUpdating();
  const user = useCurrentUser();

  const isGroupActionsEnabled = grouped && user.hasSubordinates;

  const handleSelect = (_, preset) => {
    onClose();
    onApply({ preset });
    updateUsageDatePreset(preset);
  };

  const handleTabChange = (_e, newActiveTabId) =>
    setActiveTabId(newActiveTabId);

  const handleManageGroups = () => {
    showChildComponent(ManageGroups);
  };

  const groupActions = useMemo(() => {
    const result = [];
    if (isGroupActionsEnabled && isGoGameEnv) {
      result.push(ACTION_SHARE_GROUP);
    }
    return result;
  }, [isGroupActionsEnabled]);

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      open={open}
      width={700}
      fetching={isRefetching}
      loading={isLoading || isUpdating}
    >
      <SidePage.Title
        title={activePreset?.title ? 'Preset' : 'Select preset'}
        subTitle={activePreset?.title}
        entityId={activePreset?.id}
      />
      {isGoGameEnv ? (
        <React.Fragment>
          <Box maxWidth={'50%'}>
            <Tabs
              value={activeTabId}
              onChange={handleTabChange}
              variant={'fullWidth'}
            >
              <Tab value={OWN} label={'My'} />
              <Tab value={SHARED} label={'Public'} />
            </Tabs>
          </Box>
          <Divider />
        </React.Fragment>
      ) : null}

      {isGoGameEnv ? (
        <LegacySwitchView activeId={activeTabId}>
          <LegacySwitchView.Fragment viewId={OWN}>
            <SidePage.NewBody disableGutters={true} dense={true} scroll={false}>
              {open ? (
                <PresetList
                  withAddPreset={true}
                  presets={ownPresets}
                  onItemClick={handleSelect}
                  onManageGroups={handleManageGroups}
                  onClose={onClose}
                  setOrderBy={setOrderBy}
                  groupActionLimit={0}
                  groupActions={groupActions}
                  isManageGroups={isManageGroups}
                />
              ) : null}
              {childComponent}
            </SidePage.NewBody>
          </LegacySwitchView.Fragment>
          <LegacySwitchView.Fragment viewId={SHARED}>
            <SidePage.NewBody disableGutters={true} dense={true} scroll={false}>
              {open ? (
                <PresetList
                  presets={publicPresets}
                  onItemClick={handleSelect}
                  setOrderBy={setOrderBy}
                  withOwnerName
                />
              ) : null}
            </SidePage.NewBody>
          </LegacySwitchView.Fragment>
        </LegacySwitchView>
      ) : (
        <SidePage.NewBody disableGutters={true} dense={true} scroll={false}>
          {open ? (
            <PresetList
              withAddPreset={true}
              presets={ownPresets}
              onItemClick={handleSelect}
              onManageGroups={handleManageGroups}
              onClose={onClose}
              setOrderBy={setOrderBy}
              groupActionLimit={0}
              groupActions={groupActions}
              isManageGroups={isManageGroups}
            />
          ) : null}
          {childComponent}
        </SidePage.NewBody>
      )}
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
    </SidePage>
  );
};

export default PresetsSidePage;
